import React, { useState, useEffect, useCallback, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import employeesService from '../services/employeesService';
import { useNotification } from '../context/NotificationContext';
import { AuthContext } from '../context/AuthContext';
import ConfirmActionModal from '../components/ConfirmActionModal';
import SortableHeader from '../components/SortableHeader';
import '../styles/general.css';

const PER_PAGE = 50;

// ID, Full Name, RFC, CURP, NSS, Role, Actions — the Actions column is
// always rendered (View is available to everyone), so the count is fixed.
const COLUMN_COUNT = 7;

const EmployeesListPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { notify } = useNotification();
  // Gated on the module permission rather than is_staff: Contador reads the
  // roster to pick who to pay and must not edit it, which the staff flag
  // cannot express. UX only — the API rejects the writes regardless.
  const { canManage } = useContext(AuthContext);
  const canWrite = canManage && canManage('employees');

  const [employees, setEmployees] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);

  const [search, setSearch] = useState('');
  const [roleId, setRoleId] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  const [ordering, setOrdering] = useState('-created_at');

  const [togglingEmployee, setTogglingEmployee] = useState(null);
  const [processing, setProcessing] = useState(false);

  const totalPages = Math.ceil(totalCount / PER_PAGE);

  const loadEmployees = useCallback(async () => {
    try {
      setLoading(true);
      const res = await employeesService.listEmployees({
        search: search || null,
        roleId: roleId || null,
        showInactive,
        ordering,
        page
      });
      setEmployees(res.data.results || []);
      setTotalCount(res.data.count || 0);
    } catch (err) {
      console.error('Error loading employees:', err);
      notify.error(t('common.failedToLoad'));
    } finally {
      setLoading(false);
    }
  }, [search, roleId, showInactive, ordering, page, notify, t]);

  useEffect(() => {
    loadEmployees();
  }, [loadEmployees]);

  useEffect(() => {
    const loadRoles = async () => {
      try {
        const res = await employeesService.listRoles();
        setRoles(res.data || []);
      } catch (err) {
        console.error('Error loading roles:', err);
      }
    };
    loadRoles();
  }, []);

  const handleFilterChange = (setter) => (value) => {
    setter(value);
    setPage(1);
  };

  // SortableHeader renders the arrow but does not own the state: clicking the
  // active column flips its direction, a new column starts ascending.
  const handleSort = (field) => {
    setOrdering((current) => (current === field ? `-${field}` : field));
    setPage(1);
  };

  const handleToggleActiveConfirm = async () => {
    if (!togglingEmployee) return;
    setProcessing(true);
    try {
      await employeesService.toggleActive(togglingEmployee.id);
      notify.success(
        togglingEmployee.is_active
          ? t('employees.employeeDeactivated')
          : t('employees.employeeActivated')
      );
      setTogglingEmployee(null);
      loadEmployees();
    } catch (err) {
      console.error('Error toggling employee active state:', err);
      notify.error(t('common.failedToUpdate'));
    } finally {
      setProcessing(false);
    }
  };

  if (loading && employees.length === 0) {
    return <div className="loading">{t('common.loading')}</div>;
  }

  return (
    <div className="page-container-wide">
      <div className="page-header">
        <h1>{t('employees.title')}</h1>
        {canWrite && (
          <button onClick={() => navigate('/employees/create')} className="btn btn-primary">
            {t('employees.createButton')}
          </button>
        )}
      </div>

      <div className="filter-panel">
        <div className="filter-row">
          <div className="filter-group">
            <label className="form-label" htmlFor="employee-search">
              {t('filters.searchValue')}
            </label>
            <input
              id="employee-search"
              type="text"
              value={search}
              onChange={(e) => handleFilterChange(setSearch)(e.target.value)}
              placeholder={t('employees.filters.searchPlaceholder')}
              className="form-input"
            />
          </div>

          <div className="filter-group">
            <label className="form-label" htmlFor="employee-role">
              {t('table.role')}
            </label>
            <select
              id="employee-role"
              value={roleId}
              onChange={(e) => handleFilterChange(setRoleId)(e.target.value)}
              className="form-select"
            >
              <option value="">{t('employees.filters.allRoles')}</option>
              {roles.map((role) => (
                <option key={role.id} value={role.id}>{role.name}</option>
              ))}
            </select>
          </div>

          <div className="filter-group filter-group-checkbox">
            <label className="form-label" htmlFor="employee-show-inactive">
              <input
                id="employee-show-inactive"
                type="checkbox"
                checked={showInactive}
                onChange={(e) => handleFilterChange(setShowInactive)(e.target.checked)}
              />
              {' '}{t('filters.showInactive')}
            </label>
          </div>
        </div>
      </div>

      <div className="table-wrapper">
        <table className="table">
          <thead>
            <tr>
              <th>ID</th>
              <SortableHeader
                field="last_name"
                label={t('table.fullName')}
                sortBy={ordering}
                onSort={handleSort}
              />
              <th>{t('employees.taxId')}</th>
              <SortableHeader
                field="national_id"
                label={t('employees.nationalId')}
                sortBy={ordering}
                onSort={handleSort}
              />
              <SortableHeader
                field="social_security_number"
                label={t('employees.socialSecurityNumber')}
                sortBy={ordering}
                onSort={handleSort}
              />
              <th>{t('table.role')}</th>
              <th>{t('common.actions')}</th>
            </tr>
          </thead>
          <tbody>
            {employees.length === 0 ? (
              <tr>
                <td colSpan={COLUMN_COUNT} className="empty-state">
                  <p>{t('employees.noEmployees')}</p>
                </td>
              </tr>
            ) : (
              employees.map((employee) => (
                <tr key={employee.id}>
                  <td>{employee.id}</td>
                  <td>
                    {employee.full_name}
                    {!employee.is_active && (
                      <span className="tag-badge">{t('employees.inactiveBadge')}</span>
                    )}
                  </td>
                  <td>{employee.tax_id || '—'}</td>
                  <td>{employee.national_id}</td>
                  <td>{employee.social_security_number}</td>
                  <td>{employee.role_name}</td>
                  <td className="table-actions">
                    <button
                      onClick={() => navigate(`/employees/${employee.id}`)}
                      className="btn btn-info btn-small"
                    >
                      {t('employees.viewButton')}
                    </button>
                    {canWrite && employee.is_active && (
                      <button
                        onClick={() => navigate(`/employees/${employee.id}/edit`)}
                        className="btn btn-primary btn-small"
                      >
                        {t('common.edit')}
                      </button>
                    )}
                    {canWrite && (
                      <button
                        onClick={() => setTogglingEmployee(employee)}
                        className={`btn btn-small ${employee.is_active ? 'btn-danger' : 'btn-info'}`}
                      >
                        {employee.is_active
                          ? t('employees.deactivateButton')
                          : t('employees.activateButton')}
                      </button>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className="pagination">
          <button
            onClick={() => setPage(Math.max(1, page - 1))}
            disabled={page === 1}
            className="btn btn-primary"
          >
            {t('common.previous')}
          </button>
          <span className="pagination-info">{t('common.page')} {page}</span>
          <button
            onClick={() => setPage(Math.min(totalPages, page + 1))}
            disabled={page === totalPages}
            className="btn btn-primary"
          >
            {t('common.next')}
          </button>
        </div>
      )}

      <ConfirmActionModal
        isOpen={!!togglingEmployee}
        onClose={() => setTogglingEmployee(null)}
        onConfirm={handleToggleActiveConfirm}
        title={togglingEmployee?.is_active
          ? t('employees.deactivateButton')
          : t('employees.activateButton')}
        message={togglingEmployee?.is_active
          ? t('employees.confirmDeactivate')
          : t('employees.confirmActivate')}
        confirmLabel={t('common.confirm')}
        confirming={processing}
      />
    </div>
  );
};

export default EmployeesListPage;
